import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { apiReports } from "../api/reports";
import { ManagerAttendance } from "./AttendanceDetails";

// Compact tracking-header prototype; gated server-side by role and entitlement.
// Attendance is separate from recorded engagement; neither establishes payroll hours.
const hours = (v) => `${Number(v || 0).toFixed(1)}h`;
const timeET = (d) => d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/New_York" });
const button = "tw-rounded tw-px-3 tw-py-2 tw-text-sm focus:tw-ring-2 focus:tw-ring-blue-500";
const cookieColors = ["#b78bd7", "#80bdf1", "#9ecb83", "#ddad72"];

function Cookie({ person }) {
  const fill = Math.min(1, Math.max(0, Number(person.engagedHours) || 0) / 12);
  const color = cookieColors[person.userId % cookieColors.length];
  return <span role="img" aria-label={`${hours(person.engagedHours)} of 12 engaged hours; ${Math.round(fill * 100)}% filled`}
    title={`${hours(person.engagedHours)} engaged · full cookie = 12 hours`}
    className="tw-inline-flex tw-flex-shrink-0 tw-w-8 tw-h-8 tw-rounded-full tw-border tw-border-gray-300"
    style={{ background: `conic-gradient(${color} ${fill * 360}deg, #eeeae3 0deg)` }}>
    <svg viewBox="0 0 32 32" aria-hidden="true" className="tw-w-full tw-h-full" fill="#655141" fillOpacity="0.65">
      <circle cx="11" cy="7" r="1.7" /><circle cx="23" cy="10" r="2" /><circle cx="7" cy="17" r="1.5" />
      <circle cx="17" cy="16" r="2.2" /><circle cx="24" cy="23" r="1.5" /><circle cx="13" cy="25" r="1.8" />
      <circle cx="18" cy="5" r="0.8" /><circle cx="5" cy="11" r="0.7" /><circle cx="21" cy="28" r="0.7" />
    </svg>
  </span>;
}

function Presence({ person }) {
  const label = person.online == null ? "Activity unavailable" : person.online ? "Recent activity" : "No recent activity";
  return <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-600"
    title="Online = recorded activity within the last 10 minutes. Checked every 60 seconds while this page is visible.">
    <span aria-hidden="true" className={`tw-inline-block tw-w-2 tw-h-2 tw-rounded-full ${person.online ? "tw-bg-green-500" : "tw-bg-gray-400"}`} />
    {label}
  </span>;
}

function ActivityTimeline({ detail, loading, error }) {
  const segments = !loading && !error && Array.isArray(detail?.activityTimeline?.segments) ? detail.activityTimeline.segments : null;
  const label = (minute) => {
    const total = 8 * 60 + Math.floor(minute);
    const hour = Math.floor(total / 60);
    return `${hour % 12 || 12}:${String(total % 60).padStart(2, "0")}${hour >= 12 ? "pm" : "am"} ET`;
  };
  const activeMinutes = (segments || []).reduce((total, span) => total + span.endMinute - span.startMinute, 0);
  return <div className="tw-px-4 tw-pt-3 tw-pb-1">
    <div className="tw-flex tw-justify-between tw-text-xs tw-text-gray-600 tw-mb-2">
      <span>Activity · 8am–6pm ET</span>
      <span>{loading ? "Loading…" : segments == null ? "Unavailable" : segments.length ? "Engaged" : "No activity in this window"}</span>
    </div>
    <div role="img" aria-label={`Engaged activity, 8am to 6pm ET: ${segments == null ? "unavailable" : `${Math.round(activeMinutes)} minutes recorded`}`}
      className="tw-relative tw-h-3 tw-rounded-sm tw-bg-gray-200 tw-overflow-hidden">
      {[0, 20, 40, 60, 80].map((left) => <span key={left} aria-hidden="true" className="tw-absolute tw-h-full tw-border-l tw-border-white" style={{ left: `${left}%` }} />)}
      {(segments || []).map((span, i) => <span key={i} title={`${label(span.startMinute)} – ${label(span.endMinute)}`}
        className="tw-absolute tw-h-full tw-bg-green-600"
        style={{ left: `${span.startMinute / 6}%`, width: `${(span.endMinute - span.startMinute) / 6}%` }} />)}
    </div>
    <div className="tw-flex tw-justify-between tw-text-gray-500 tw-mt-1" style={{ fontSize: 10 }} aria-hidden="true">
      {["8am", "10am", "12pm", "2pm", "4pm", "6pm"].map((tick) => <span key={tick}>{tick}</span>)}
    </div>
  </div>;
}

function PersonDetail({ person, detail, loading, error, onClose }) {
  const day = detail?.days?.[0];
  const metrics = [
    ["Engaged time", hours(person.engagedHours)],
    ["In-app time", hours(person.pageHours)],
    ["Call talk time", hours(person.callHours)],
    ["Policies scheduled", person.scheduled],
    ["Texts sent", person.texts],
    ["Calls / missed", `${person.calls} / ${person.missed}`],
    ["Report saves / QA edits", person.reportEdits],
  ];
  return <section aria-label={`${person.name} today`} className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-shadow-lg tw-overflow-y-auto"
    style={{ maxHeight: "calc(100vh - 220px)" }}>
    <div className="tw-flex tw-items-center tw-gap-3 tw-p-4 tw-bg-blue-50 tw-border-b tw-border-gray-200">
      <Cookie person={person} />
      <div className="tw-flex-1"><strong className="tw-text-sm">{person.name}</strong><div><Presence person={person} /> <span className="tw-text-xs tw-text-gray-500">· Today ET</span></div></div>
      <button type="button" onClick={onClose} aria-label="Close scheduler details" className={button}>✕</button>
    </div>
    <ActivityTimeline detail={detail} loading={loading} error={error} />
    <dl className="tw-px-4 tw-py-2">
      {metrics.map(([label, value]) => <div key={label} className="tw-flex tw-justify-between tw-gap-4 tw-py-2 tw-border-b tw-border-gray-100 tw-text-sm">
        <dt className="tw-text-gray-600">{label}</dt><dd className="tw-font-semibold tw-m-0" style={{ fontVariantNumeric: "tabular-nums" }}>{value ?? "—"}</dd>
      </div>)}
    </dl>
    <div className="tw-p-4 tw-pt-1 tw-text-xs tw-text-gray-600" aria-live="polite">
      {loading ? "Loading activity times…" : error || (day
        ? <div className="tw-flex tw-justify-between"><span>First activity <strong>{day.first} ET</strong></span><span>Latest <strong>{day.last} ET</strong></span></div>
        : "No recorded activity times today.")}
      <p className="tw-mt-3 tw-mb-0">Recent activity means a recorded event within 10 minutes. Attendance and breaks are shown separately below.</p>
    </div>
    <ManagerAttendance userId={person.userId} />
  </section>;
}

export default function SchedulerHeaderPrototype() {
  const [people, setPeople] = useState([]);
  const [selected, setSelected] = useState(null);
  const [revision, setRevision] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [asOf, setAsOf] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [clockNote, setClockNote] = useState(false);
  const [popoverLeft, setPopoverLeft] = useState(0);
  const header = useRef(null);
  const strip = useRef(null);
  const requestPending = useRef(false);

  useEffect(() => {
    let cancelled = false;
    requestPending.current = true;
    setLoading(true);
    setError("");
    apiReports.getSchedulerActivityToday().then((response) => {
      if (response.data.status !== 200) {throw new Error("Unable to load today's activity.");}
      if (!cancelled) {
        setPeople((response.data.result.schedulers || []).slice().sort((a, b) => a.name.localeCompare(b.name)));
        setAsOf(new Date());
      }
    }).catch(() => {
      if (!cancelled) {
        setPeople([]);
        setSelected(null);
        setAsOf(null);
        setError("Today's activity could not be verified. No totals are shown; try updating again.");
      }
    }).finally(() => {
      if (!cancelled) {requestPending.current = false; setLoading(false);}
    });
    return () => { cancelled = true; };
  }, [revision]);

  useEffect(() => {
    const update = () => {
      if (!document.hidden && !requestPending.current) {setRevision((v) => v + 1);}
    };
    const timer = window.setInterval(update, 60000);
    document.addEventListener("visibilitychange", update);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", update);
    };
  }, []);

  useEffect(() => {
    if (selected == null) {return;}
    let cancelled = false;
    setDetail(null);
    setDetailLoading(true);
    setDetailError("");
    apiReports.getSchedulerActivityToday(selected).then((response) => {
      if (response.data.status !== 200) {throw new Error("Unable to load activity times.");}
      if (!cancelled) {setDetail(response.data.result);}
    }).catch(() => {
      if (!cancelled) {setDetailError("Activity times could not be loaded.");}
    }).finally(() => { if (!cancelled) {setDetailLoading(false);} });
    return () => { cancelled = true; };
  }, [selected, revision]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") {setSelected(null);}
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useLayoutEffect(() => {
    if (selected == null) {return;}
    const scroller = strip.current;
    const reposition = () => {
      const anchor = scroller.querySelector('[aria-expanded="true"]');
      if (!anchor) {return;}
      const parent = header.current.getBoundingClientRect();
      const bounds = anchor.getBoundingClientRect();
      const width = Math.min(360, parent.width, window.innerWidth - 16);
      const min = Math.max(0, 8 - parent.left);
      const max = Math.max(min, Math.min(parent.width, window.innerWidth - 8 - parent.left) - width);
      setPopoverLeft(Math.max(min, Math.min(bounds.left - parent.left, max)));
    };
    reposition();
    scroller.addEventListener("scroll", reposition, { passive: true });
    window.addEventListener("resize", reposition);
    return () => {
      scroller.removeEventListener("scroll", reposition);
      window.removeEventListener("resize", reposition);
    };
  }, [selected]);

  const person = people.find((p) => p.userId === selected);
  const detailPanel = person ? <PersonDetail key={`${selected}-${revision}`} person={person}
    detail={detail} loading={detailLoading || detail?.userId !== selected && !detailError}
    error={detailError} onClose={() => setSelected(null)} /> : null;

  return <section aria-label="Scheduler tracking header prototype" className="tw-relative tw-my-4 tw-text-gray-800">
    <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-mb-2">
      <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs">
        <strong className="tw-bg-yellow-100 tw-text-yellow-900 tw-rounded tw-px-2 tw-py-1">Design prototype</strong>
        <span>Today ET · {asOf ? `updated ${timeET(asOf)} ET` : loading ? "loading" : "unavailable"} · auto 60s</span>
      </div>
      <div className="tw-flex tw-gap-2">
        <button type="button" className={`${button} tw-text-gray-600`} onClick={() => setClockNote(!clockNote)} aria-expanded={clockNote}>Clock rules</button>
        <button type="button" disabled={loading} className={`${button} tw-bg-blue-50 tw-text-blue-900`} onClick={() => setRevision((v) => v + 1)}>
          {loading ? "Loading…" : "Update activity"}
        </button>
      </div>
    </div>
    {clockNote && <aside className="tw-p-3 tw-mb-3 tw-text-sm tw-bg-yellow-50 tw-border-l-4 tw-border-yellow-400">
      <strong>Attendance and activity are separate.</strong> The attendance clock automatically ends after 15 minutes without qualifying activity,
      backdated to the last confirmed activity. Explicit breaks are exempt; employee corrections retain an audit trail.
      Engaged activity remains an estimate and ongoing periods may arrive later. Neither measure proves attendance or determines pay.
    </aside>}
    {error && <p role="alert" className="tw-text-red-700 tw-p-3 tw-bg-red-50">{error}</p>}
    {!loading && !error && !people.length && <p className="tw-p-3 tw-bg-gray-50">No schedulers configured for this company.</p>}

    <div ref={header} className="tw-relative">
      <div className="tw-flex tw-items-stretch tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-sm">
        <div className="tw-flex tw-items-center tw-px-4 tw-text-sm tw-font-semibold tw-border-r tw-border-gray-200">Schedulers<br />today</div>
        <button type="button" className="tw-px-2 tw-text-blue-800" aria-label="Previous schedulers"
          onClick={() => strip.current.scrollBy({ left: -260, behavior: "smooth" })}>‹</button>
        <div ref={strip} className="tw-flex tw-flex-1 tw-overflow-x-auto tw-py-1">
          {people.map((p) => <button key={p.userId} type="button" onClick={() => setSelected(selected === p.userId ? null : p.userId)}
            aria-expanded={selected === p.userId} aria-label={`${p.name}: ${hours(p.engagedHours)} engaged, show details`}
            className={`tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-2 tw-border-r tw-border-gray-100 tw-text-left tw-flex-shrink-0
              focus:tw-ring-2 focus:tw-ring-inset focus:tw-ring-blue-500 ${selected === p.userId ? "tw-bg-blue-50" : "hover:tw-bg-gray-50"}`}>
            <Cookie person={p} />
            <div><strong className="tw-block tw-text-sm tw-truncate" title={p.name} style={{ maxWidth: 125 }}>{p.name}</strong>
              <Presence person={p} /></div>
            <strong className="tw-text-sm tw-ml-2">{hours(p.engagedHours)}</strong>
          </button>)}
        </div>
        <button type="button" className="tw-px-2 tw-text-blue-800" aria-label="Next schedulers"
          onClick={() => strip.current.scrollBy({ left: 260, behavior: "smooth" })}>›</button>
      </div>
      {detailPanel && <div className="tw-absolute tw-top-full tw-mt-2 tw-z-30"
        style={{ left: popoverLeft, width: "min(360px, 100%, calc(100vw - 16px))" }}>{detailPanel}</div>}
    </div>
  </section>;
}
